import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetch, apiFetchJson } from '../../apiClient';
import BookCover from './BookCover';
import './MoodDiscovery.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

const MOODS = [
  { key: 'Cozy', emoji: '☕', label: 'Cozy' },
  { key: 'Thrilling', emoji: '⚡', label: 'Thrilling' },
  { key: 'Mind-Expanding', emoji: '🧠', label: 'Mind-Expanding' },
  { key: 'Funny', emoji: '😂', label: 'Funny' },
  { key: 'Emotional', emoji: '💕', label: 'Emotional' },
  { key: 'Dark', emoji: '🌑', label: 'Dark' },
  { key: 'Inspiring', emoji: '✨', label: 'Inspiring' },
  { key: 'Adventurous', emoji: '🗺️', label: 'Adventurous' },
  { key: 'Relaxing', emoji: '🌊', label: 'Relaxing' },
  { key: 'Intense', emoji: '🔥', label: 'Intense' },
  { key: 'Thought-Provoking', emoji: '💭', label: 'Thought-Provoking' },
  { key: 'Heartwarming', emoji: '🥰', label: 'Heartwarming' },
];

function DifficultyDots({ level }) {
  if (!level && level !== 0) return null;
  const rounded = Math.round(level);
  return (
    <span className="mood-discovery-difficulty-dots" title={`Difficulty: ${level}/5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={i < rounded ? 'mood-discovery-dot-filled' : 'mood-discovery-dot-empty'}>●</span>
      ))}
    </span>
  );
}

function MoodDiscovery({ factFictionMode, navigateTo }) {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const [selectedMood, setSelectedMood] = useState(null);
  const [results, setResults] = useState([]);
  const [trending, setTrending] = useState([]);
  const [moodTags, setMoodTags] = useState([]);
  const [difficulty, setDifficulty] = useState(3);
  const [loading, setLoading] = useState(false);
  const [trendingLoading, setTrendingLoading] = useState(true);
  const [taggingBookId, setTaggingBookId] = useState(null);
  const [actionStatus, setActionStatus] = useState({});
  const [expandedBookId, setExpandedBookId] = useState(null);
  const [error, setError] = useState(null);
  const [hasLangPrefs, setHasLangPrefs] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  // Check language preferences
  useEffect(() => {
    const check = async () => {
      try {
        const d = await apiFetchJson(instance, '/api/readerprofile');
        setHasLangPrefs(!!d.preferredLanguages);
      } catch { /* ignore */ }
    };
    check();
  }, [instance]);

  // Load mood tags and trending on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [tagsRes, trendingRes] = await Promise.all([
          apiFetch(instance, '/api/mood/tags'),
          apiFetch(instance, '/api/mood/trending'),
        ]);
        if (cancelled) return;
        if (tagsRes.ok) {
          const data = await tagsRes.json();
          setMoodTags(unwrap(data));
        }
        if (trendingRes.ok) {
          const data = await trendingRes.json();
          setTrending(unwrap(data));
        }
      } catch {
        if (!cancelled) setError('Failed to load mood data.');
      } finally {
        if (!cancelled) setTrendingLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [instance]);

  const discover = useCallback(async (mood, diff, page = 1, append = false) => {
    if (!mood) return;
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setCurrentPage(1);
        setTotalPages(1);
      }
      setError(null);
      const typeParam = factFictionMode || 'fiction';
      const data = await apiFetchJson(
        instance,
        `/api/mood/discover?mood=${encodeURIComponent(mood)}&type=${encodeURIComponent(typeParam)}&difficulty=${diff}&page=${page}&pageSize=20`
      );
      const books = unwrap(data.books ?? data);
      if (append) {
        setResults(prev => [...prev, ...books]);
      } else {
        setResults(books);
      }
      setCurrentPage(data.page || page);
      setTotalPages(data.totalPages || 1);
    } catch {
      setError('Failed to discover books.');
      if (!append) setResults([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [instance, factFictionMode]);

  const handleMoodClick = useCallback((moodKey) => {
    setSelectedMood(moodKey);
    discover(moodKey, difficulty);
  }, [discover, difficulty]);

  const handleLoadMore = useCallback(() => {
    if (currentPage < totalPages && !loadingMore) {
      discover(selectedMood, difficulty, currentPage + 1, true);
    }
  }, [discover, selectedMood, difficulty, currentPage, totalPages, loadingMore]);

  const handleDifficultyChange = useCallback((e) => {
    const val = Number(e.target.value);
    setDifficulty(val);
    if (selectedMood) {
      discover(selectedMood, val);
    }
  }, [discover, selectedMood]);

  const handleTagMood = useCallback(async (bookId) => {
    if (!selectedMood) return;
    try {
      setTaggingBookId(bookId);
      await apiFetchJson(instance, '/api/mood/tag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookId, mood: selectedMood }),
      });
    } catch {
      setError('Failed to tag mood.');
    } finally {
      setTaggingBookId(null);
    }
  }, [instance, selectedMood]);

  const setBookStatus = useCallback(async (bookId, status) => {
    const key = status === 'Finished' ? `read-${bookId}` : `wtr-${bookId}`;
    setActionStatus(prev => ({ ...prev, [key]: 'loading' }));
    try {
      const res = await apiFetch(instance, '/api/userbookstatus', {
        method: 'POST',
        body: JSON.stringify({ bookId, status }),
      });
      if (res.ok) {
        setActionStatus(prev => ({ ...prev, [key]: 'success' }));
      } else if (res.status === 409) {
        const updateRes = await apiFetch(instance, `/api/userbookstatus/${bookId}`, {
          method: 'PUT',
          body: JSON.stringify({ status }),
        });
        setActionStatus(prev => ({ ...prev, [key]: updateRes.ok ? 'success' : 'error' }));
      } else {
        setActionStatus(prev => ({ ...prev, [key]: 'error' }));
      }
    } catch {
      setActionStatus(prev => ({ ...prev, [key]: 'error' }));
    }
  }, [instance]);

  const addToLibrary = useCallback(async (bookId) => {
    setActionStatus(prev => ({ ...prev, [`lib-${bookId}`]: 'loading' }));
    try {
      const res = await apiFetch(instance, '/api/userlibrary', {
        method: 'POST',
        body: JSON.stringify({ bookId, ownership: 'Owned' }),
      });
      setActionStatus(prev => ({ ...prev, [`lib-${bookId}`]: (res.ok || res.status === 409) ? 'success' : 'error' }));
    } catch {
      setActionStatus(prev => ({ ...prev, [`lib-${bookId}`]: 'error' }));
    }
  }, [instance]);

  const addToWishlist = useCallback(async (bookId) => {
    setActionStatus(prev => ({ ...prev, [`wish-${bookId}`]: 'loading' }));
    try {
      const res = await apiFetch(instance, '/api/userlibrary', {
        method: 'POST',
        body: JSON.stringify({ bookId, ownership: 'Wishlist' }),
      });
      setActionStatus(prev => ({ ...prev, [`wish-${bookId}`]: (res.ok || res.status === 409) ? 'success' : 'error' }));
    } catch {
      setActionStatus(prev => ({ ...prev, [`wish-${bookId}`]: 'error' }));
    }
  }, [instance]);

  const dismissBook = useCallback(async (bookId) => {
    setActionStatus(prev => ({ ...prev, [`dismiss-${bookId}`]: 'loading' }));
    try {
      const res = await apiFetch(instance, '/api/userbookstatus', {
        method: 'POST',
        body: JSON.stringify({ bookId, status: 'NotInterested' }),
      });
      if (res.ok || res.status === 409) {
        if (res.status === 409) {
          await apiFetch(instance, `/api/userbookstatus/${bookId}`, {
            method: 'PUT',
            body: JSON.stringify({ status: 'NotInterested' }),
          });
        }
        setActionStatus(prev => ({ ...prev, [`dismiss-${bookId}`]: 'success' }));
        setTimeout(() => setResults(prev => prev.filter(b => (b.id || b.bookId) !== bookId)), 800);
      } else {
        setActionStatus(prev => ({ ...prev, [`dismiss-${bookId}`]: 'error' }));
      }
    } catch {
      setActionStatus(prev => ({ ...prev, [`dismiss-${bookId}`]: 'error' }));
    }
  }, [instance]);

  const formatReaderCount= (count) => {
    if (!count) return null;
    if (count >= 1000) return `${(count / 1000).toFixed(count >= 10000 ? 0 : 1)}k`;
    return String(count);
  };

  return (
    <div className="mood-discovery-container">
      <div className="mood-discovery-main">
        <h2 className="mood-discovery-header">{t('bookClub.moodDiscovery.title')}</h2>

        {!hasLangPrefs && (
          <div className="mood-discovery-lang-hint" onClick={() => navigateTo && navigateTo('profile')}>
            🌍 Seeing books in many languages? Set your preferred languages in <strong>Profile</strong> →
          </div>
        )}

        {error && (
          <div className="mood-discovery-error">
            <span>⚠️ {error}</span>
            <button onClick={() => setError(null)}>{t('bookClub.common.dismiss')}</button>
          </div>
        )}

        {/* Mood Picker */}
        <div className="mood-discovery-picker">
          {MOODS.map((m) => (
            <button
              key={m.key}
              className={`mood-discovery-pill mood-discovery-pill--${m.key.toLowerCase().replace(/[^a-z]/g, '')} ${selectedMood === m.key ? 'mood-discovery-pill--active' : ''}`}
              onClick={() => handleMoodClick(m.key)}
            >
              <span className="mood-discovery-pill-emoji">{m.emoji}</span>
              {m.label}
            </button>
          ))}
        </div>

        {/* Difficulty Filter */}
        <div className="mood-discovery-difficulty">
          <label className="mood-discovery-difficulty-label">
            {t('bookClub.moodDiscovery.difficulty')} <strong>{difficulty}</strong>/5
          </label>
          <input
            type="range"
            min="1"
            max="5"
            step="1"
            value={difficulty}
            onChange={handleDifficultyChange}
            className="mood-discovery-slider"
          />
        </div>

        {/* Results */}
        {loading ? (
          <div className="mood-discovery-loading">
            <div className="mood-discovery-spinner" />
            <span>{t('bookClub.moodDiscovery.finding')}</span>
          </div>
        ) : selectedMood && results.length === 0 ? (
          <div className="mood-discovery-empty">
            <span className="mood-discovery-empty-icon">📚</span>
            <p>{t('bookClub.moodDiscovery.noResults', { mood: selectedMood, difficulty })}</p>
            <p>{t('bookClub.moodDiscovery.tryAdjusting')}</p>
          </div>
        ) : results.length > 0 ? (
          <div className="mood-discovery-results">
            {results.map((book) => (
              <div key={book.id || book.bookId} className={`mood-discovery-card ${expandedBookId === (book.id || book.bookId) ? 'mood-discovery-card--expanded' : ''}`}>
                <div className="mood-discovery-card-clickable" onClick={() => setExpandedBookId(prev => prev === (book.id || book.bookId) ? null : (book.id || book.bookId))} role="button" tabIndex={0}>
                  <BookCover src={book.coverUrl} alt={book.title} className="mood-discovery-card-cover" />
                  <div className="mood-discovery-card-info">
                    <h3 className="mood-discovery-card-title" title={book.title}>{book.title}</h3>
                    <p className="mood-discovery-card-author">{book.author}</p>
                    <div className="mood-discovery-card-meta">
                      <DifficultyDots level={book.difficulty} />
                      {book.averageRating > 0 && (
                        <span className="mood-discovery-card-rating">⭐ {book.averageRating}{book.ratingCount > 0 ? ` (${book.ratingCount})` : ''}</span>
                      )}
                      {book.source === 'community' && (
                        <span className="mood-discovery-card-badge mood-discovery-badge-community">🏷️ {t('bookClub.moodDiscovery.communityPick')}</span>
                      )}
                    </div>
                    {book.readerCount > 0 && (
                      <p className="mood-discovery-card-social">
                        {formatReaderCount(book.readerCount)} readers found this {selectedMood}
                      </p>
                    )}
                  </div>
                  <span className="mood-discovery-card-chevron">{expandedBookId === (book.id || book.bookId) ? '▲' : '▼'}</span>
                </div>
                {expandedBookId === (book.id || book.bookId) && (
                  <div className="mood-discovery-card-detail">
                    {book.description ? (
                      <p className="mood-discovery-card-desc">{book.description.length > 200 ? book.description.slice(0, 200) + '…' : book.description}</p>
                    ) : (
                      <p className="mood-discovery-card-desc mood-discovery-card-desc--empty">No description available.</p>
                    )}
                    {book.pageCount > 0 && <span className="mood-discovery-card-pages">📄 {book.pageCount} pages</span>}
                  </div>
                )}
                <div className="mood-discovery-card-actions">
                    <button
                      className={`mood-discovery-action-btn mood-discovery-action-btn--wtr ${actionStatus[`wtr-${book.id || book.bookId}`] || ''}`}
                      onClick={() => setBookStatus(book.id || book.bookId, 'WantToRead')}
                      disabled={actionStatus[`wtr-${book.id || book.bookId}`] === 'loading' || actionStatus[`wtr-${book.id || book.bookId}`] === 'success'}
                    >
                      {actionStatus[`wtr-${book.id || book.bookId}`] === 'loading' ? '⏳' :
                       actionStatus[`wtr-${book.id || book.bookId}`] === 'success' ? `✓ ${t('bookClub.bookSearch.added')}` :
                       actionStatus[`wtr-${book.id || book.bookId}`] === 'error' ? '⚠ Retry' : `📌 ${t('bookClub.myBooks.tbr')}`}
                    </button>
                    <button
                      className={`mood-discovery-action-btn mood-discovery-action-btn--read ${actionStatus[`read-${book.id || book.bookId}`] || ''}`}
                      onClick={() => setBookStatus(book.id || book.bookId, 'Finished')}
                      disabled={actionStatus[`read-${book.id || book.bookId}`] === 'loading' || actionStatus[`read-${book.id || book.bookId}`] === 'success'}
                    >
                      {actionStatus[`read-${book.id || book.bookId}`] === 'loading' ? '⏳' :
                       actionStatus[`read-${book.id || book.bookId}`] === 'success' ? `✓ ${t('bookClub.wishlist.read')}!` :
                       actionStatus[`read-${book.id || book.bookId}`] === 'error' ? '⚠ Retry' : `✓ ${t('bookClub.wishlist.read')}`}
                    </button>
                    <button
                      className={`mood-discovery-action-btn mood-discovery-action-btn--lib ${actionStatus[`lib-${book.id || book.bookId}`] || ''}`}
                      onClick={() => addToLibrary(book.id || book.bookId)}
                      disabled={actionStatus[`lib-${book.id || book.bookId}`] === 'loading' || actionStatus[`lib-${book.id || book.bookId}`] === 'success'}
                    >
                      {actionStatus[`lib-${book.id || book.bookId}`] === 'loading' ? '⏳' :
                       actionStatus[`lib-${book.id || book.bookId}`] === 'success' ? `✓ ${t('bookClub.moodDiscovery.owned')}` :
                       actionStatus[`lib-${book.id || book.bookId}`] === 'error' ? '⚠ Retry' : `📚 ${t('bookClub.moodDiscovery.own')}`}
                    </button>
                    <button
                      className={`mood-discovery-action-btn mood-discovery-action-btn--wish ${actionStatus[`wish-${book.id || book.bookId}`] || ''}`}
                      onClick={() => addToWishlist(book.id || book.bookId)}
                      disabled={actionStatus[`wish-${book.id || book.bookId}`] === 'loading' || actionStatus[`wish-${book.id || book.bookId}`] === 'success'}
                      title="Add to birthday/buy wishlist"
                    >
                      {actionStatus[`wish-${book.id || book.bookId}`] === 'loading' ? '⏳' :
                       actionStatus[`wish-${book.id || book.bookId}`] === 'success' ? `✓ ${t('bookClub.moodDiscovery.wished')}` :
                       actionStatus[`wish-${book.id || book.bookId}`] === 'error' ? '⚠ Retry' : `🎁 ${t('bookClub.moodDiscovery.wish')}`}
                    </button>
                    <button
                      className="mood-discovery-action-btn mood-discovery-action-btn--tag"
                      onClick={() => handleTagMood(book.id || book.bookId)}
                      disabled={taggingBookId === (book.id || book.bookId)}
                    >
                      {taggingBookId === (book.id || book.bookId) ? '⏳' : `🏷️ ${t('bookClub.moodDiscovery.tag')}`}
                    </button>
                    <button
                      className={`mood-discovery-action-btn mood-discovery-action-btn--dismiss ${actionStatus[`dismiss-${book.id || book.bookId}`] || ''}`}
                      onClick={() => dismissBook(book.id || book.bookId)}
                      disabled={actionStatus[`dismiss-${book.id || book.bookId}`] === 'loading' || actionStatus[`dismiss-${book.id || book.bookId}`] === 'success'}
                      title="Never suggest this book again"
                    >
                      {actionStatus[`dismiss-${book.id || book.bookId}`] === 'loading' ? '⏳' :
                       actionStatus[`dismiss-${book.id || book.bookId}`] === 'success' ? `✓ ${t('bookClub.recommendations.hidden')}` : '🚫'}
                    </button>
                  </div>
              </div>
            ))}
            {currentPage < totalPages && (
              <div className="mood-discovery-load-more">
                <button
                  className="mood-discovery-load-more-btn"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <><span className="mood-discovery-spinner mood-discovery-spinner--small" /> Loading…</>
                  ) : (
                    `${t('bookClub.moodDiscovery.showMoreBooks')} (page ${currentPage} of ${totalPages})`
                  )}
                </button>
              </div>
            )}
          </div>
        ) : !selectedMood ? (
          <div className="mood-discovery-empty">
            <span className="mood-discovery-empty-icon">👆</span>
            <p>{t('bookClub.moodDiscovery.selectMood')}</p>
          </div>
        ) : null}
      </div>

      {/* Trending Sidebar */}
      <aside className="mood-discovery-sidebar">
        <h3 className="mood-discovery-sidebar-title">🔥 {t('bookClub.moodDiscovery.trendingMoods')}</h3>
        {trendingLoading ? (
          <div className="mood-discovery-sidebar-loading">
            <div className="mood-discovery-spinner mood-discovery-spinner--small" />
          </div>
        ) : trending.length === 0 ? (
          <p className="mood-discovery-sidebar-empty">{t('bookClub.moodDiscovery.noTrending')}</p>
        ) : (
          <ul className="mood-discovery-trending-list">
            {trending.map((m, i) => (
              <li key={m.mood || i} className="mood-discovery-trending-item">
                <button
                  className="mood-discovery-trending-btn"
                  onClick={() => handleMoodClick(m.mood)}
                >
                  <span className="mood-discovery-trending-name">{m.mood} {t('bookClub.moodDiscovery.readsAreTrending')} 📈</span>
                  {m.count > 0 && (
                    <span className="mood-discovery-trending-count">{formatReaderCount(m.count)}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        {moodTags.length > 0 && (
          <>
            <h4 className="mood-discovery-sidebar-subtitle">{t('bookClub.moodDiscovery.allMoodTags')}</h4>
            <div className="mood-discovery-sidebar-tags">
              {moodTags.map((tag, i) => (
                <span key={tag.name || i} className="mood-discovery-sidebar-tag">{tag.name || tag}</span>
              ))}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

export default MoodDiscovery;
